import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import CloseButton from './CloseButton.Container';
import NextButton from './NextButton.Container';
import type { OwnProps as NextButtonProps } from './NextButton.View';
import type { Delay } from '../../utils/animation';
import type { PositionProps } from '../../utils/styledcomponents';

interface ContainerProps {
	light: boolean;
	small: boolean;
}

const Container = styled.div<ContainerProps>`
	background-color: ${(props) =>
		props.light ? props.theme.colors.blue : props.theme.colors.darkBlue};
	border-radius: 18.5vmin;
	bottom: ${(props) => (props.small ? '10vh' : '5.5vh')};
	box-shadow: ${(props) => props.theme.shadow.dialog};
	color: ${(props) => props.theme.colors.white};
	left: ${(props) => (props.small ? '25vw' : '3vw')};
	position: absolute;
	right: ${(props) => (props.small ? '25vw' : '3vw')};
	top: ${(props) => (props.small ? '10vh' : '5.5vh')};
	will-change: opacity, transform;
`;

export const Clip = styled.div`
	border-radius: 18.5vmin;
	bottom: 0;
	left: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	top: 0;
`;

type NextProps = Omit<NextButtonProps, keyof PositionProps>;

const Next: FunctionComponent<NextProps> = (props: NextProps) => {
	return <NextButton position="absolute" right="0" bottom="-1%" {...props} />;
};

interface CloseProps {
	onClose?: () => void;
	highlightClose?: boolean;
	highlightCloseDelay?: Delay;
}

const Close: FunctionComponent<CloseProps> = (props: CloseProps) => {
	const { onClose, highlightClose, highlightCloseDelay } = props;

	if (!onClose) {
		return null;
	}

	return (
		<CloseButton
			onClick={onClose}
			position="absolute"
			right="2vw"
			top="2vh"
			highlighted={highlightClose}
			highlightDelay={highlightCloseDelay}
		/>
	);
};

export interface Props {
	light?: boolean;
	small?: boolean;
	closeButton?: ReactNode;
}

const Dialog: FunctionComponent<Props> = (props: PropsWithChildren<Props>) => {
	const { children, light = false, small = false, closeButton } = props;

	return (
		<Container light={light} small={small}>
			{children}
			{closeButton}
		</Container>
	);
};

export default Dialog;
export { Close, Next };
